import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';

import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { ArticleResponse, ArticleType } from '../types';

const UpdateArticle: React.FC = () => {

  const [description, setDescription] = useState('');
  const [type, setType] = useState<ArticleType>(ArticleType.URL);
  const [link, setLink] = useState('');
  const [tag, setTag] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams(); // article ID from route

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await api.get<ArticleResponse>(`/articles/${id}`);
        const article = response.data;
        setDescription(article.description);
        setType(article.type);
        setLink(article.link);
          setTag(article.tag);
        setExistingImageUrl(article.image); // Change according to your backend
      } catch (err: any) {
        setRequestError("Erreur lors du chargement de l’article.");
      }
    };
    fetchArticle();
  }, [id]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setImage(file);
    setImageError(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRequestError(null);
    setLoading(true);

    const formData = new FormData();
    formData.append('description', description);
    formData.append('type', type);
    formData.append('link', link);
     formData.append('tag', tag);
    if (image) {
        console.log('image modified')
        formData.append('image', image);
    }



    try {
      await api.put(`/articles/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setOpenSnackbar(true);
      setTimeout(() => navigate('/articles'), 1500);
    } catch (error: any) {

      console.log(error)

      const status = error.response?.status || '';
      const message =
        error.response?.data?.error ||
        error.message ||
        'Error when editing article.';


      setRequestError(status + ' | ' +message);
      setTimeout(() => setRequestError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxWidth="600px" mx="auto" mt={4}>
      <Typography variant="h5" gutterBottom>
        Edit Article Details
      </Typography>

      {requestError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {requestError}
        </Alert>
      )}

      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <TextField
          label="Description"
          fullWidth
          margin="normal"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          disabled={loading}
        />

        <TextField
          label="Type"
          select
          fullWidth
          margin="normal"
          value={type}
          onChange={(e) => setType(e.target.value as ArticleType)}
          required
          disabled={loading}
        >
          <MenuItem value={ArticleType.URL}>URL</MenuItem>
          <MenuItem value={ArticleType.AUDIO}>AUDIO</MenuItem>
          <MenuItem value={ArticleType.VIDEO}>VIDEO</MenuItem>
        </TextField>

        <TextField
          label="Link"
          type="url"
          fullWidth
          margin="normal"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          disabled={loading}
          required
        />

         <TextField
          label="Tag"
          type="text"
          fullWidth
          margin="normal"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          disabled={loading}
      
        />

        {image ? (
          <Box display="flex" flexDirection="column" alignItems="center" my={2}>
            <img
              src={URL.createObjectURL(image)}
              alt="Preview"
              style={{ width: 200, height: 150, objectFit: 'cover', marginBottom: 8 }}
            />
            <Button variant="outlined" color="error" onClick={() => setImage(null)} disabled={loading}>
              Delete image
            </Button>
          </Box>
        ) : existingImageUrl ? (
          <Box display="flex" flexDirection="column" alignItems="center" my={2}>
            <img
              src={existingImageUrl}
              alt="Article actuel"
              style={{ width: 200, height: 150, objectFit: 'cover', marginBottom: 8 }}
            />
            <Button variant="outlined" component="label" disabled={loading}>
              Change image
              <input type="file" hidden accept="image/*" onChange={handleImageChange} />
            </Button>
          </Box>
        ) : (
          <Box my={2}>
            <Button variant="outlined" component="label" fullWidth disabled={loading}>
              choose image
              <input type="file" hidden accept="image/*" onChange={handleImageChange} />
            </Button>
            {imageError && (
              <Typography variant="body2" color="error" mt={1}>
                image is required
              </Typography>
            )}
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Updating ...' : 'Update'}
        </Button>
      </form>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={1500}
        message="Article mis à jour avec succès"
        onClose={() => setOpenSnackbar(false)}
      />
    </Box>
  );
};

export default UpdateArticle;
